/**
 * Absence Management — types, leave-type registry, and the working-days helper.
 *
 * v1 scope: request → manager approval/reject → owner cancel. NO accrual,
 * balances, policies, public holidays, half-days, or calendar yet — those are
 * later increments. Working days = Mon–Fri inclusive (holidays not yet excluded).
 */
import { resolveWorkTimeEntry, workingFractionOfWeek, } from './employment.js';
export const ABSENCE_STATUSES = ['submitted', 'approved', 'rejected', 'cancelled'];
/** Built-in defaults — used when `config.absenceLeaveTypes` is absent. A config
 *  editor (Policy Manager) will let HR customise these later. */
export const DEFAULT_LEAVE_TYPES = [
    { name: 'Vacation', paid: true, deductsBalance: true },
    { name: 'Work from anywhere', paid: true },
    { name: 'Sick leave', paid: true },
    { name: 'Overtime compensation', paid: true, overtimeCompensation: true },
    { name: 'Unpaid', paid: false, requiresReason: true },
    { name: 'Special leave', paid: true, requiresReason: true },
];
export const DEFAULT_SICK_CERT_REMINDERS = { employeeDays: [2, 5], hrAfterDays: 7 };
export function sickCertReminderConfig(config) {
    const c = config?.absenceSickCertReminders;
    if (!c)
        return DEFAULT_SICK_CERT_REMINDERS;
    const days = [...new Set((c.employeeDays ?? []).filter((d) => Number.isInteger(d) && d >= 1 && d <= 60))].sort((a, b) => a - b);
    return { employeeDays: days, hrAfterDays: Number.isInteger(c.hrAfterDays) && c.hrAfterDays >= 1 ? c.hrAfterDays : DEFAULT_SICK_CERT_REMINDERS.hrAfterDays };
}
/** Whole days a certificate has been outstanding (from the request's creation). */
export function sickCertDaysPending(createdAtMs, nowMs = Date.now()) {
    return Math.max(0, Math.floor((nowMs - createdAtMs) / 86_400_000));
}
/** A request whose certificate the employee still owes. */
export const isSickCertOutstanding = (r) => r.sickCertMode === 'upload_later' && r.sickCertStatus === 'pending' && r.status !== 'cancelled' && r.status !== 'rejected';
/** The certificate-free day limit for a user's entity (null = not configured
 *  → no certificate requirement, sick requests behave like before). */
export function sickCertDaysForEntity(config, entity) {
    const groups = config?.absenceSickCert ?? [];
    const exact = entity ? groups.find((g) => g.entity === entity) : undefined;
    const fallback = groups.find((g) => g.entity === '');
    return (exact ?? fallback)?.days ?? null;
}
/** True for the sick leave type ('Sick leave'; legacy 'Sick' variants too). */
export function isSickLeaveType(typeName) {
    return !!typeName && /sick/i.test(typeName);
}
/** The kinds available to a user: exact entity match, else the '' default. */
export function specialLeaveKindsForEntity(config, entity) {
    const groups = config?.absenceSpecialLeave ?? [];
    const exact = entity ? groups.find((g) => g.entity === entity) : undefined;
    const fallback = groups.find((g) => g.entity === '');
    return (exact ?? fallback)?.kinds ?? [];
}
/** True if `typeName` is the (configurable) special-leave type. */
export function isSpecialLeaveType(leaveTypes, typeName) {
    if (!typeName)
        return false;
    const t = leaveTypes.find((x) => x.name === typeName);
    if (t && t.specialLeave !== undefined)
        return !!t.specialLeave;
    return typeName === 'Special leave';
}
/** True if `typeName` is the (configurable) overtime-compensation leave type. */
export function isOvertimeCompensationType(leaveTypes, typeName) {
    if (!typeName)
        return false;
    const t = leaveTypes.find((x) => x.name === typeName);
    if (t)
        return !!t.overtimeCompensation;
    return typeName === 'Overtime compensation';
}
/** Primary policy ids a dependent policy follows (new list + legacy single id). */
export function dependentPrimaryIds(p) {
    if (!p)
        return [];
    return [...new Set([...(p.dependsOnPolicyIds ?? []), ...(p.dependsOnPolicyId ? [p.dependsOnPolicyId] : [])])];
}
/** True for a top-up add-on (fills up to a total over any primary). */
export function isTopUpPolicy(p) {
    return p?.topUpToDays != null && Number.isFinite(p.topUpToDays);
}
/** Dependent = follows named primaries, or a top-up add-on (follows any primary). Attached via `dependentPolicyAssignments`. */
export function isDependentPolicy(p) {
    return dependentPrimaryIds(p).length > 0 || isTopUpPolicy(p);
}
/** A dependent policy's days for a person: flat annual days, or for a top-up add-on
 *  the gap between the person's primary policy and the add-on's total. */
export function dependentEntitlementDays(dependent, primary) {
    if (isTopUpPolicy(dependent))
        return Math.max(0, (dependent.topUpToDays ?? 0) - (primary?.annualEntitlementDays ?? 0));
    return dependent.annualEntitlementDays;
}
export const resolveHrPolicies = (config) => config?.absenceHrPolicies ?? [];
/** Per-assignment consumption from the person's requests (approved only — HR records them approved). */
export function hrPolicyStatuses(user, config, requests, todayISO) {
    const policies = resolveHrPolicies(config);
    return (user.hrPolicyAssignments ?? []).flatMap((a) => {
        const policy = policies.find((p) => p.id === a.policyId);
        if (!policy)
            return [];
        let taken = 0;
        let planned = 0;
        for (const r of requests) {
            if (r.hrPolicyId !== a.policyId || r.status !== 'approved')
                continue;
            if (r.endDate < a.from || r.startDate > a.to)
                continue;
            if (r.endDate < todayISO)
                taken += r.workingDays;
            else
                planned += r.workingDays;
        }
        const remaining = policy.trackDays && a.days != null ? a.days - taken - planned : null;
        const phase = todayISO < a.from ? 'upcoming' : todayISO > a.to ? 'ended' : 'active';
        return [{ policy, assignment: a, taken, planned, remaining, phase }];
    });
}
/** Name of the holiday on `date` in `calendar` (full or half day), or null. */
export function holidayNameForDate(calendar, date, company) {
    return (company?.find((c) => c.monthDay === date.slice(5))?.name ??
        calendar?.holidays?.find((h) => h.date === date)?.name ??
        null);
}
/** True when `date` is a half-day holiday in `holidays` (built by `holidaySet`). */
export function isHalfHoliday(holidays, date) {
    return !!holidays?.half?.has(date);
}
export function resolveHolidayCalendars(config) {
    return config?.holidayCalendars ?? [];
}
export function findHolidayCalendar(config, id) {
    if (!id)
        return null;
    return resolveHolidayCalendars(config).find((c) => c.id === id) ?? null;
}
export const MONTH_DAY_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
/** A date Set that also answers `has()` for recurring month-days (company holidays). */
class HolidayDateSet extends Set {
    monthDays;
    constructor(dates, monthDays) {
        super(dates);
        this.monthDays = monthDays;
    }
    has(date) {
        return super.has(date) || (this.monthDays.size > 0 && this.monthDays.has(date.slice(5)));
    }
}
/**
 * Build the holiday dates of a calendar (or null): full days in the Set, half days in
 * `.half`. `company` (config.companyHolidays) adds the recurring company-wide days for
 * every year; a full company day wins over a half-day calendar entry on the same date.
 */
export function holidaySet(calendar, company) {
    const list = calendar?.holidays ?? [];
    const comp = (company ?? []).filter((c) => MONTH_DAY_RE.test(c.monthDay));
    const fullMd = new Set(comp.filter((c) => !c.halfDay).map((c) => c.monthDay));
    const halfMd = new Set(comp.filter((c) => c.halfDay && !fullMd.has(c.monthDay)).map((c) => c.monthDay));
    const set = new HolidayDateSet(list.filter((h) => !h.halfDay).map((h) => h.date), fullMd);
    set.half = new HolidayDateSet(list.filter((h) => h.halfDay && !fullMd.has(h.date.slice(5))).map((h) => h.date), halfMd);
    return set;
}
/** The holidays that apply to a person: their calendar (by id) plus the company-wide days. */
export function holidaysFor(config, calendarId) {
    return holidaySet(findHolidayCalendar(config, calendarId), config?.companyHolidays);
}
/** The company-wide days of one year as dated entries (for calendars and lists). */
export function companyHolidayEntries(company, year) {
    return (company ?? [])
        .filter((c) => MONTH_DAY_RE.test(c.monthDay))
        .map((c) => (c.halfDay ? { date: `${year}-${c.monthDay}`, name: c.name, halfDay: true } : { date: `${year}-${c.monthDay}`, name: c.name }));
}
export function resolveAbsencePolicies(config) {
    return config?.absencePolicies ?? [];
}
export function findAbsencePolicy(config, id) {
    if (!id)
        return null;
    return resolveAbsencePolicies(config).find((p) => p.id === id) ?? null;
}
/**
 * The holiday calendar a person should get when none is set (Oct 2026): the calendar
 * named like their work location, else the single calendar of their country (a country
 * with several calendars, e.g. the German states, stays unassigned for HR to pick).
 */
export function defaultHolidayCalendarFor(config, person) {
    const cals = resolveHolidayCalendars(config);
    const loc = (person.workLocation ?? '').trim().toLowerCase();
    if (loc) {
        const byName = cals.find((c) => c.name.trim().toLowerCase() === loc);
        if (byName)
            return byName;
    }
    const country = (person.country ?? '').trim().toUpperCase();
    if (country) {
        const byCountry = cals.filter((c) => (c.country ?? '').trim().toUpperCase() === country);
        if (byCountry.length === 1)
            return byCountry[0] ?? null;
    }
    return null;
}
/** The dependent policy attached to the user on `onDate` whose primary is the
 *  user's effective policy that day (else null). */
export function effectiveDependentPolicy(user, config, onDate) {
    const primaryId = effectiveAbsencePolicyId(user, onDate);
    if (!primaryId)
        return null;
    const active = (user.dependentPolicyAssignments ?? []).filter((a) => a.effectiveFrom <= onDate).sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
    for (let i = active.length - 1; i >= 0; i -= 1) {
        const p = findAbsencePolicy(config, active[i].policyId);
        if (p && (isTopUpPolicy(p) || dependentPrimaryIds(p).includes(primaryId)))
            return p;
    }
    return null;
}
/** Waterfall attribution of one leave type across a primary and a dependent
 *  entitlement: approved requests first (by start date), then pending, each
 *  filling the primary until its total is reached and overflowing into the
 *  dependent. Computed at read time, so cancelling a primary request moves
 *  dependent days back automatically (Michael's rule: the primary is always
 *  fully consumed before the dependent shows any usage). */
export function vacationWaterfall(requests, typeName, primaryTotal, dependentTotal, year, todayISO, holidays) {
    const { start, end } = periodOf(year);
    // A request crossing the year's edge counts with its days inside the year only (requestDaysInPeriod).
    const inYear = requests
        .filter((r) => r.type === typeName && (r.status === 'approved' || r.status === 'submitted'))
        .map((r) => ({ ...r, workingDays: requestDaysInPeriod(r, start, end, holidays) }))
        .filter((r) => r.workingDays > 0);
    const order = [...inYear.filter((r) => r.status === 'approved'), ...inYear.filter((r) => r.status === 'submitted')]
        .map((r, i) => ({ r, i })).sort((a, b) => (a.r.status !== b.r.status ? 0 : a.r.startDate < b.r.startDate ? -1 : a.r.startDate > b.r.startDate ? 1 : a.i - b.i)).map((x) => x.r);
    const mk = (total) => ({ total, taken: 0, plannedApproved: 0, plannedRequested: 0, remaining: total });
    const primary = mk(primaryTotal);
    const dependent = mk(dependentTotal);
    let left = primaryTotal;
    const add = (s, r, d) => {
        if (d <= 0)
            return;
        if (r.status === 'submitted')
            s.plannedRequested += d;
        else if (r.endDate < todayISO)
            s.taken += d;
        else
            s.plannedApproved += d;
    };
    for (const r of order) {
        const toPrimary = Math.max(0, Math.min(r.workingDays, left));
        left -= toPrimary;
        add(primary, r, toPrimary);
        add(dependent, r, r.workingDays - toPrimary);
    }
    primary.remaining = primaryTotal - primary.taken - primary.plannedApproved - primary.plannedRequested;
    dependent.remaining = dependentTotal - dependent.taken - dependent.plannedApproved - dependent.plannedRequested;
    return { primary, dependent, dependentOpen: left <= 0 };
}
/** The assignment list, synthesizing a baseline entry from a legacy
 *  `absencePolicyId` when no `policyAssignments` exist. Sorted by date asc. */
export function resolvePolicyAssignments(user) {
    const list = user.policyAssignments && user.policyAssignments.length > 0
        ? [...user.policyAssignments]
        : user.absencePolicyId
            ? [{ policyId: user.absencePolicyId, effectiveFrom: '2000-01-01' }]
            : [];
    return list.sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
}
/** Effective policy id on `onDate` (YYYY-MM-DD), or null. */
export function effectiveAbsencePolicyId(user, onDate) {
    const active = resolvePolicyAssignments(user).filter((a) => a.effectiveFrom <= onDate);
    return active.length > 0 ? active[active.length - 1].policyId : null;
}
/** The assignment in effect on `onDate` (latest `effectiveFrom ≤ onDate`), or null.
 *  A legacy baseline synthesized from a bare `absencePolicyId` has `effectiveFrom`
 *  '2000-01-01' (= "no recorded start date"). */
export function currentPolicyAssignment(user, onDate) {
    const active = resolvePolicyAssignments(user).filter((a) => a.effectiveFrom <= onDate);
    return active.length > 0 ? active[active.length - 1] : null;
}
/** effectiveFrom of the synthesized legacy baseline (no recorded start date). */
export const LEGACY_POLICY_EFFECTIVE_FROM = '2000-01-01';
/** Match a CSV cell against a config list by id first, then by name
 *  (case-insensitive). Ambiguous names (two entries share it) don't match. */
export function matchByIdOrName(list, ref) {
    const r = ref.trim();
    if (!r)
        return null;
    const byId = list.find((x) => x.id === r);
    if (byId)
        return byId;
    const byName = list.filter((x) => x.name.trim().toLowerCase() === r.toLowerCase());
    return byName.length === 1 ? byName[0] : null;
}
/** Assignments scheduled to start AFTER `onDate` (the "Future" list). */
export function futurePolicyAssignments(user, onDate) {
    return (user.policyAssignments ?? []).filter((a) => a.effectiveFrom > onDate);
}
/** Carry-over days credited for `year` (0 if none). */
export function carryOverForYear(user, year) {
    return user.vacationCarryOver?.find((c) => c.year === year)?.days ?? 0;
}
/**
 * Pro-rated upfront entitlement for someone whose FIRST policy assignment falls
 * within `year`: annual × (remaining half-months ÷ 24) from `startDate` to
 * Dec 31. The start month counts 2 half-months if the start day ≤ 15, else 1.
 * Started before the year → full annual; after → 0. Rounded to the half-day.
 */
export function proRateUpfrontDays(annual, startDate, year) {
    const startYear = Number(startDate.slice(0, 4));
    if (startYear < year)
        return annual;
    if (startYear > year)
        return 0;
    return proRateUpfrontDaysRange(annual, startDate, `${year}-12-31`);
}
/** Half-months (of 24/year) overlapped by [fromDate, toDate], using the same
 *  1–15 / 16–end split. Assumes both dates are in the same calendar year. */
export function halfMonthsInRange(fromDate, toDate) {
    // Walks month by month from `fromDate`'s month to `toDate`'s month (may cross a
    // year boundary — Oct–Sep leave years), counting overlapped half-months.
    let y = Number(fromDate.slice(0, 4));
    let m = Number(fromDate.slice(5, 7));
    const endY = Number(toDate.slice(0, 4));
    const endM = Number(toDate.slice(5, 7));
    let count = 0;
    for (let guard = 0; guard < 36 && (y < endY || (y === endY && m <= endM)); guard++) {
        const mm = String(m).padStart(2, '0');
        const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
        const halves = [
            [`${y}-${mm}-01`, `${y}-${mm}-15`],
            [`${y}-${mm}-16`, `${y}-${mm}-${String(last).padStart(2, '0')}`],
        ];
        for (const [hs, he] of halves) {
            if (fromDate <= he && toDate >= hs)
                count++;
        }
        m += 1;
        if (m > 12) {
            m = 1;
            y += 1;
        }
    }
    return count;
}
/** Pro-rated upfront days earned over [from, to] (same year). Rounded to ½. */
export function proRateUpfrontDaysRange(annual, from, to) {
    if (to < from)
        return 0;
    return Math.round(((annual * halfMonthsInRange(from, to)) / 24) * 2) / 2;
}
/** Round a policy-swap date to a clean entitlement boundary so swapped policies
 *  don't double-count: day 1–15 → the 1st of the month; day 16–end → the 16th
 *  (the half-month split). The prior policy then ends the day before. */
export function roundSwapDate(dateStr) {
    const day = Number(dateStr.slice(8, 10));
    return `${dateStr.slice(0, 7)}-${day <= 15 ? '01' : '16'}`;
}
function prevDayISO(dateStr) {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}
const minISO = (a, b) => (a < b ? a : b);
/**
 * Monthly accrual (Oct 2026, Mexico on Remote.com): one twelfth of the annual
 * entitlement is granted on the 1st of each month of the segment [from, to],
 * up to and including the month of `todayISO` (a segment that starts mid-month
 * still earns that month's twelfth, as Remote does). Hour precision (⅛ day),
 * not half-days, so the figure matches Remote's hours.
 * Viewed after the segment ends → every month of the segment; before it starts → 0.
 */
export function monthlyAccruedDays(annual, fromISO, toISO, todayISO) {
    const upTo = minISO(toISO, todayISO);
    if (upTo < fromISO.slice(0, 7) + '-01')
        return 0;
    const [fy, fm] = [Number(fromISO.slice(0, 4)), Number(fromISO.slice(5, 7))];
    const [uy, um] = [Number(upTo.slice(0, 4)), Number(upTo.slice(5, 7))];
    const months = Math.min(12, Math.max(0, (uy - fy) * 12 + (um - fm) + 1));
    return Math.round(((annual * months) / 12) * 8) / 8;
}
/**
 * Daily accrual (Oct 2026, every accruing policy): the annual entitlement is earned a share per CALENDAR day of the
 * segment [from, to], up to and including `todayISO`, as Remote.com accrues. The share is 1 / the length of the
 * calendar year the segment starts in (365 or 366). Hour precision (⅛ day). Viewed after the segment ends → the whole
 * segment; before it starts → 0.
 */
export function dailyAccruedDays(annual, fromISO, toISO, todayISO) {
    const upTo = minISO(toISO, todayISO);
    if (upTo < fromISO)
        return 0;
    const days = Math.round((Date.parse(`${upTo}T00:00:00Z`) - Date.parse(`${fromISO}T00:00:00Z`)) / 86_400_000) + 1;
    const y = Number(fromISO.slice(0, 4));
    const yearLen = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 366 : 365;
    return Math.round(((annual * Math.min(days, yearLen)) / yearLen) * 8) / 8;
}
/** Accrued days of one policy over a segment, by its method (upfront = the pro-rated full amount at once). */
export function accruedDaysForSegment(pol, fromISO, toISO, todayISO) {
    if (pol.accrualMethod === 'daily')
        return dailyAccruedDays(pol.annualEntitlementDays, fromISO, toISO, todayISO);
    if (pol.accrualMethod === 'monthly')
        return monthlyAccruedDays(pol.annualEntitlementDays, fromISO, toISO, todayISO);
    return proRateUpfrontDaysRange(pol.annualEntitlementDays, fromISO, toISO);
}
/** The user's effective policy on `todayISO` when it ACCRUES over time (monthly or daily); null for upfront. */
export function accruingPolicy(user, config, todayISO) {
    const pol = findAbsencePolicy(config, effectiveAbsencePolicyId(user, todayISO));
    return pol && pol.accrualMethod !== 'upfront' ? pol : null;
}
/** @deprecated name kept for existing callers — any accruing policy (monthly or daily), see accruingPolicy. */
export const accruesMonthly = accruingPolicy;
/**
 * A user's vacation entitlement for `year` + carry-over. `null` when no policy.
 *
 * Single policy in the year → the effective policy's annual days (upfront pro-
 * rated when the first assignment is mid-year; monthly accrual = the twelfths
 * granted up to `todayISO`, see `monthlyAccruedDays`). When a POLICY SWAP
 * happens within the year, each policy segment is pro-rated by the time it
 * applied (swap boundaries rounded via `roundSwapDate`), so there's no double
 * entitlement across the swap.
 */
export function vacationEntitlementDays(user, config, year, todayISO) {
    const assignments = resolvePolicyAssignments(user);
    if (assignments.length === 0)
        return null;
    const { start: yStart, end: yEnd } = periodOf(year);
    const carry = carryOverForYear(user, typeof year === 'number' ? year : year.yearKey);
    // Rounded, sorted timeline → year segments (each policy's slice of the year).
    const tl = assignments
        .map((a) => ({ policyId: a.policyId, from: roundSwapDate(a.effectiveFrom) }))
        .sort((a, b) => a.from.localeCompare(b.from));
    const segs = [];
    for (let i = 0; i < tl.length; i++) {
        const from = tl[i].from > yStart ? tl[i].from : yStart;
        const next = i + 1 < tl.length ? tl[i + 1].from : null;
        const to = next ? minISO(prevDayISO(next), yEnd) : yEnd;
        if (to < yStart || from > yEnd || from > to)
            continue;
        segs.push({ policyId: tl[i].policyId, from, to });
    }
    if (segs.length === 0)
        return carry; // no policy applied during the year
    if (segs.length === 1) {
        const pol = findAbsencePolicy(config, segs[0].policyId);
        if (!pol)
            return null;
        const accruing = pol.accrualMethod !== 'upfront';
        const base = accruedDaysForSegment(pol, segs[0].from, segs[0].to, todayISO);
        // Pro-rate by the TIME-WEIGHTED working fraction over the segment (a mid-year
        // move to/from part-time is weighted by how long each arrangement applied).
        const fraction = base * averageWorkingFraction(user, segs[0].from, segs[0].to);
        return (accruing ? roundEighth(fraction) : roundHalf(fraction)) + carry;
    }
    // Swap year: sum each policy's time-pro-rated slice (no double entitlement).
    let total = 0;
    let anyAccruing = false;
    for (const s of segs) {
        const pol = findAbsencePolicy(config, s.policyId);
        if (!pol)
            continue;
        anyAccruing ||= pol.accrualMethod !== 'upfront';
        total += accruedDaysForSegment(pol, s.from, s.to, todayISO) * averageWorkingFraction(user, s.from, s.to);
    }
    return (anyAccruing ? roundEighth(total) : roundHalf(total)) + carry;
}
/** The entitlement for the WHOLE leave year (what an accruing policy will have granted by its last day), carry-over
 *  included. Equals vacationEntitlementDays for upfront policies. */
export function vacationAnnualEntitlementDays(user, config, year) {
    const end = typeof year === 'number' ? `${year}-12-31` : year.end;
    return vacationEntitlementDays(user, config, year, end);
}
/** Round to ⅛ day (one hour of an 8-hour day) — accruing policies keep Remote's hour precision. */
function roundEighth(n) {
    return Math.round(n * 8) / 8;
}
/** Round to the nearest 0.5 (vacation days are granted/taken in half-days). */
function roundHalf(n) {
    return Math.round(n * 2) / 2;
}
/** Day-weighted average working fraction over [fromISO,toISO] inclusive — averages
 *  `workingFractionOfWeek(effective work-time entry)` across the calendar days, so
 *  a mid-year switch to/from part-time is weighted by how long each applied.
 *  Full-time / no arrangement → 1.0. */
function averageWorkingFraction(user, fromISO, toISO) {
    const start = Date.parse(`${fromISO}T00:00:00Z`);
    const end = Date.parse(`${toISO}T00:00:00Z`);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
        return workingFractionOfWeek(resolveWorkTimeEntry(user, fromISO));
    }
    let sum = 0;
    let n = 0;
    for (let t = start; t <= end; t += 86_400_000) {
        const iso = new Date(t).toISOString().slice(0, 10);
        sum += workingFractionOfWeek(resolveWorkTimeEntry(user, iso));
        n += 1;
    }
    return n > 0 ? sum / n : 1;
}
/** Upsert (one per policy) or remove an assignment; returns the new list. */
export function applyPolicyAssignment(current, change) {
    const next = current.filter((a) => a.policyId !== change.policyId);
    if (!change.remove && change.effectiveFrom) {
        next.push({ policyId: change.policyId, effectiveFrom: change.effectiveFrom });
    }
    return next.sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
}
export function leaveYearBounds(startMonth, todayISO) {
    const m = startMonth && startMonth >= 2 && startMonth <= 12 ? Math.floor(startMonth) : 1;
    const y = Number(todayISO.slice(0, 4));
    if (m === 1)
        return { start: `${y}-01-01`, end: `${y}-12-31`, yearKey: y, label: String(y), calendar: true };
    const mm = String(m).padStart(2, '0');
    const startYear = todayISO >= `${y}-${mm}-01` ? y : y - 1;
    const endDate = new Date(Date.UTC(startYear + 1, m - 1, 0)); // day before the next start
    const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return {
        start: `${startYear}-${mm}-01`,
        end: endDate.toISOString().slice(0, 10),
        yearKey: startYear,
        label: `${MON[m - 1]} ${startYear} – ${MON[(m + 10) % 12]} ${startYear + 1}`,
        calendar: false,
    };
}
/** The leave year of the user's effective primary policy on `todayISO`. */
export function leaveYearForUser(user, config, todayISO) {
    const pol = findAbsencePolicy(config, effectiveAbsencePolicyId(user, todayISO));
    return leaveYearBounds(pol?.leaveYearStartMonth ?? null, todayISO);
}
/** Calendar year number → its bounds (backwards-compatible call sites pass a number). */
export function periodOf(year) {
    return typeof year === 'number' ? { start: `${year}-01-01`, end: `${year}-12-31` } : year;
}
/**
 * The working days of a request that fall inside `[start, end]` (27 Oct 2026). A request inside the
 * period counts whole; one that crosses the period's edge (New Year, or 1 Oct for an Oct–Sep leave
 * year) is split by date, so the days in January / October belong to the new year — as Remote.com
 * and Kenjo count them. With `holidays` the split is exact (the person's calendar); without, the
 * weekdays of each part decide.
 *
 * The stored `workingDays` fill the request from its FIRST day (30 Oct 2026): the part before the period
 * takes its own exact count, the part inside gets what is left, capped at its own exact count. A request
 * booked in the hub has stored = exact, so this is the plain date split. An IMPORTED request whose source
 * calendar knew more days off than ours (Kenjo's Christmas closure: 24 Dec – 3 Jan stored as 2 days where
 * we count 5) then keeps its days in the year it started in, instead of a proportional share rounded to
 * the half day (which charged Christele, Falco and Gloria 0.5 of a Christmas-2025 request to 2026). A
 * half-day start/end lands in the part that contains it.
 */
export function requestDaysInPeriod(r, start, end, holidays) {
    if (r.endDate < start || r.startDate > end)
        return 0;
    if (r.startDate >= start && r.endDate <= end)
        return r.workingDays;
    const hol = holidays ?? undefined;
    const from = r.startDate < start ? start : r.startDate;
    const to = r.endDate > end ? end : r.endDate;
    const halfStart = r.halfDayStart ? 0.5 : 0;
    const halfEnd = r.halfDayEnd ? 0.5 : 0;
    // Exact count of the part before the period (its half-day start, if any, belongs to it).
    const before = r.startDate < start ? Math.max(0, countWorkingDays(r.startDate, prevDayISO(start), hol) - halfStart) : 0;
    // Exact count of the part inside the period, minus the half-day flags that fall inside it.
    const inside = Math.max(0, countWorkingDays(from, to, hol) - (r.startDate >= start ? halfStart : 0) - (r.endDate <= end ? halfEnd : 0));
    if (inside <= 0)
        return 0;
    return Math.max(0, Math.min(inside, r.workingDays - before));
}
export function computeCategorySummary(requests, typeName, total, year, todayISO, 
/** The person's holiday calendar — makes the split of a year-crossing request exact. */
holidays) {
    let taken = 0;
    let plannedApproved = 0;
    let plannedRequested = 0;
    const { start, end } = periodOf(year);
    for (const r of requests) {
        if (r.type !== typeName)
            continue;
        const days = requestDaysInPeriod(r, start, end, holidays);
        if (days <= 0)
            continue;
        if (r.status === 'approved') {
            if (r.endDate < todayISO)
                taken += days;
            else
                plannedApproved += days;
        }
        else if (r.status === 'submitted') {
            plannedRequested += days;
        }
    }
    const remaining = total != null ? total - taken - plannedApproved - plannedRequested : null;
    return { total, taken, plannedApproved, plannedRequested, remaining };
}
/**
 * Compute the vacation balance for a year from the user's policy + their
 * requests. Counts balance-deducting leave types only; approved → taken,
 * submitted → pending. Carry-over is deferred (0 for now). Returns null when no
 * policy is assigned.
 */
export function computeVacationBalance(policy, requests, leaveTypes, year) {
    if (!policy)
        return null;
    const deducts = new Set(leaveTypes.filter((t) => t.deductsBalance).map((t) => t.name));
    let taken = 0;
    let pending = 0;
    for (const r of requests) {
        if (!deducts.has(r.type))
            continue;
        if (r.startDate.slice(0, 4) !== String(year))
            continue;
        if (r.status === 'approved')
            taken += r.workingDays;
        else if (r.status === 'submitted')
            pending += r.workingDays;
    }
    const carriedOver = 0; // deferred to the carry-over feature
    const entitlement = policy.annualEntitlementDays;
    return {
        entitlement,
        carriedOver,
        taken,
        pending,
        remaining: entitlement + carriedOver - taken - pending,
    };
}
export function resolveVacationGroups(config) {
    return (config?.vacationSummaryGroups ?? []).filter((g) => g && g.name && Array.isArray(g.entities));
}
/** Does a user's employment entity belong to this bucket? ('' = "(No entity)"). */
export function userInVacationGroup(employmentEntity, group) {
    const e = (employmentEntity ?? '').trim().toLowerCase();
    return group.entities.some((x) => x.trim().toLowerCase() === e);
}
/** The leave year with `yearKey` for the user's effective policy: Oct–Sep policies → Oct `yearKey` – Sep
 *  `yearKey`+1; calendar policies → the calendar year. */
export function leaveYearWithKey(user, config, yearKey, todayISO) {
    const pol = findAbsencePolicy(config, effectiveAbsencePolicyId(user, todayISO));
    const m = pol?.leaveYearStartMonth && pol.leaveYearStartMonth >= 2 && pol.leaveYearStartMonth <= 12 ? pol.leaveYearStartMonth : 1;
    return leaveYearBounds(m, `${yearKey}-${String(m).padStart(2, '0')}-01`);
}
/** Quarter (1–4) of a leave year on `todayISO`; after its end → 4, before its start → 1. */
export function leaveYearQuarter(ly, todayISO) {
    if (todayISO > ly.end)
        return 4;
    if (todayISO < ly.start)
        return 1;
    const months = (Number(todayISO.slice(0, 4)) - Number(ly.start.slice(0, 4))) * 12 + (Number(todayISO.slice(5, 7)) - Number(ly.start.slice(5, 7)));
    return Math.min(4, Math.floor(months / 3) + 1);
}
export function vacationStatusRow(user, config, requests, 
/** A year KEY (summaries: the leave year starting in that year) or 'current' (warnings: the leave
 *  year containing `todayISO`, which for an Oct–Sep policy in Jan–Sep is LAST year's key). */
year, todayISO) {
    const vacationType = resolveLeaveTypes(config).find((t) => t.deductsBalance)?.name ?? 'Vacation';
    const ly = year === 'current' ? leaveYearForUser(user, config, todayISO) : leaveYearWithKey(user, config, year, todayISO);
    const entitlement = vacationEntitlementDays(user, config, ly, todayISO);
    const s = computeCategorySummary(requests, vacationType, entitlement, ly, todayISO);
    return {
        email: user.email,
        name: user.displayName || user.email,
        team: user.team || '',
        entitlement,
        consumed: s.taken,
        plannedApproved: s.plannedApproved,
        remaining: entitlement != null ? entitlement - s.taken - s.plannedApproved : null,
        plannedRequested: s.plannedRequested,
        period: ly.label,
        quarter: leaveYearQuarter(ly, todayISO),
    };
}
/** Quarter (1–4) for a date. */
export function quarterOf(date) {
    return Math.floor(date.getUTCMonth() / 3) + 1;
}
/** Vacation status flag for the warning report, by quarter OF THE ROW'S LEAVE YEAR (Oct 2026: defaults
 *  to `row.quarter`, so an Oct–Sep person in October is judged as Q1, not Q4):
 *  Q1 → issue if nothing consumed AND nothing planned-approved;
 *  Q2 → issue if consumed+planned-approved < 60% of entitlement;
 *  Q3+ → issue if more than 5 days still open (entitlement − consumed − p&a). */
export function vacationStatusFlag(row, quarter = row.quarter) {
    if (row.entitlement == null || row.entitlement <= 0)
        return 'ok';
    const usedOrPlanned = row.consumed + row.plannedApproved;
    if (quarter <= 1)
        return usedOrPlanned === 0 ? 'issue' : 'ok';
    if (quarter === 2)
        return usedOrPlanned < 0.6 * row.entitlement ? 'issue' : 'ok';
    return (row.remaining ?? 0) > 5 ? 'issue' : 'ok';
}
export function resolveLeaveTypes(config) {
    const t = config?.absenceLeaveTypes;
    return t && t.length > 0 ? t : DEFAULT_LEAVE_TYPES;
}
/**
 * Count working days (Mon–Fri) between two ISO dates (YYYY-MM-DD), inclusive.
 * Uses UTC throughout so a date-only string never shifts by timezone. Holidays
 * in `holidays` cost 0, half-day holidays (`isHalfHoliday`) 0.5.
 * Returns 0 for invalid input or end-before-start.
 */
export function countWorkingDays(startISO, endISO, holidays, 
/** Optional per-date worked-day predicate — a false weekday (e.g. a part-time
 *  day off) is skipped, exactly like a weekend/holiday, so it costs 0 vacation. */
isWorkedDay) {
    const start = new Date(`${startISO}T00:00:00Z`);
    const end = new Date(`${endISO}T00:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
        return 0;
    }
    let days = 0;
    const d = new Date(start);
    while (d <= end) {
        const dow = d.getUTCDay(); // 0 = Sun, 6 = Sat
        const iso = d.toISOString().slice(0, 10);
        const worked = dow !== 0 && dow !== 6 && !(holidays && holidays.has(iso)) && (!isWorkedDay || isWorkedDay(iso));
        if (worked)
            days += isHalfHoliday(holidays, iso) ? 0.5 : 1;
        d.setUTCDate(d.getUTCDate() + 1);
    }
    return days;
}
/**
 * Working days for an absence, accounting for half-day boundaries. A single-day
 * request with a half-day flag is 0.5; a multi-day request subtracts 0.5 for a
 * half-day start and/or end. Never returns below 0.
 */
export function computeAbsenceWorkingDays(startISO, endISO, halfDayStart, halfDayEnd, holidays, 
/** Optional worked-day predicate (part-time). Non-working weekdays cost 0. */
isWorkedDay) {
    const full = countWorkingDays(startISO, endISO, holidays, isWorkedDay);
    if (full <= 0)
        return 0;
    if (startISO === endISO) {
        return halfDayStart || halfDayEnd ? Math.min(0.5, full) : full;
    }
    let days = full;
    if (halfDayStart)
        days -= 0.5;
    if (halfDayEnd)
        days -= 0.5;
    return days < 0 ? 0 : days;
}
//# sourceMappingURL=absence.js.map